import { useEffect } from "react"

import { Navigate, Route, useLocation, useParams } from "react-router-dom"

import { AdminRoutesPaths, LegacyAdminRoutesPaths } from "../../constants/admin-routes"
import { LegacyRoutesPaths, RoutesPaths } from "../../constants/user-routes"
import { URLS } from "../LayoutOnboarding/utils/constants/quickItems"

// A redirect that keeps everything the old address carried: the query string an
// e-mail link appends, the hash, and the part of the path the pattern matched as
// a parameter or a splat.
const LegacyRedirect = ({ to, withSplat }) => {
  const { search, hash } = useLocation()
  const params = useParams()

  const target = typeof to === "function" ? to(params) : to
  // These routes live in a descendant `<Routes>` — InternalPages is itself rendered
  // by a `path="*"` route — and `useParams` merges the whole chain, so that
  // ancestor's splat is in `params` even under a pattern that declares none of its
  // own. Reading it unconditionally appended the whole pathname to the target.
  const rest = withSplat ? params["*"] : undefined

  return <Navigate to={`${target}${rest ? `/${rest}` : ""}${search}${hash}`} replace />
}

// Leaves the SPA entirely, so it replaces the location instead of navigating:
// `Navigate` only knows in-app addresses.
const ExternalRedirect = ({ to }) => {
  useEffect(() => {
    window.location.replace(to)
  }, [to])

  return null
}

const redirect = (from, to) => (
  <Route
    key={from}
    path={from}
    element={<LegacyRedirect to={to} withSplat={from.endsWith("/*")} />}
  />
)

// Where the platform's addresses used to be (CHA1-2847). Nothing links here any
// more, but bookmarks, links in e-mails already sent and links the backend still
// builds do — so these stay for good rather than for a release or two.
//
// React Router ranks by specificity, not by order, so a static old address wins
// over the `:page` patterns it looks like.
export const legacyRedirectRoutes = [
  redirect(LegacyRoutesPaths.dashboard, RoutesPaths.dashboard.root),
  // The backend sends the browser here after a top-up, with the transaction in the
  // query — which the redirect carries over to the screen that reads it.
  redirect(LegacyRoutesPaths.dashboard_thank_you, RoutesPaths.dashboard.thank_you),
  redirect(LegacyRoutesPaths.alerts, RoutesPaths.alerts.all),
  redirect(`${LegacyRoutesPaths.alert_details}/:alert_id`, ({ alert_id }) =>
    RoutesPaths.alerts.details(alert_id)
  ),

  redirect(LegacyRoutesPaths.credits, RoutesPaths.credits),

  // Receipt's Configure moved to Deflect, so it leaves the Prevent Configure
  // prefix its old address shared — matched ahead of `/configure/:page` because
  // `receipts` is a static segment.
  redirect(`${LegacyRoutesPaths.configure_receipts}/*`, RoutesPaths.receipts._configure),
  redirect(LegacyRoutesPaths.configure, RoutesPaths.configure.enrollment),
  redirect(
    `${LegacyRoutesPaths.configure}/:page`,
    ({ page }) => `${RoutesPaths.configure.root}/${page}`
  ),

  redirect(LegacyRoutesPaths.receipts, RoutesPaths.receipts.all),
  // Receipt shipped at `/receipts`; `/digital-receipt` was the SOON stub before it.
  redirect(LegacyRoutesPaths.digital_receipt, RoutesPaths.receipts.all),
  // Receipt's list became one of the Lookups (CHA1-4235).
  redirect(LegacyRoutesPaths.deflect_all, RoutesPaths.receipts.all),

  // Integrations is the Integrate tab of Configure now, and its tab slugs are the
  // same, so the splat lands on the matching tab.
  redirect(LegacyRoutesPaths.integrations, RoutesPaths.configure.processors),
  // Not a Configure tab — its own screen, which only ever shared the prefix.
  redirect(LegacyRoutesPaths.integrations_premium_features, RoutesPaths.prevent.premium_features),
  redirect(`${LegacyRoutesPaths.integrations}/*`, RoutesPaths.configure.root),

  redirect(LegacyRoutesPaths.team, RoutesPaths.settings.team),
  redirect(LegacyRoutesPaths.developers, RoutesPaths.settings.developers),
  redirect(`${LegacyRoutesPaths.developers}/*`, RoutesPaths.settings.developers),

  <Route
    key={LegacyRoutesPaths.help}
    path={LegacyRoutesPaths.help}
    element={<ExternalRedirect to={URLS.help} />}
  />
]

// Business settings → Receipt became Lookups (CHA1-4235). Mounted among the admin
// pages: the splat keeps the Configure step, `LegacyRedirect` the business_account_id.
export const adminLegacyRedirectRoutes = [
  redirect(LegacyAdminRoutesPaths.receipts_all, AdminRoutesPaths.receipts.all),
  redirect(
    `${LegacyAdminRoutesPaths.receipts_configure}/*`,
    AdminRoutesPaths.receipts.configure_business
  )
]

// Group parents: the sidebar's Alerts, Lookups and Status rows address the group,
// and the URL map has each of them open its default child rather than 404.
export const groupParentRoutes = [
  redirect(RoutesPaths.prevent.root, RoutesPaths.alerts.all),
  redirect(RoutesPaths.deflect.root, RoutesPaths.receipts.all),
  redirect(RoutesPaths.health.root, RoutesPaths.health.overview)
]
